/**
 * App Routes Configuration
 */

import { lazy, Suspense, useEffect } from "react"
import { createBrowserRouter, useNavigate, useSearchParams } from "react-router-dom"
import { ResponsiveAuthRoute } from "@/components/mobile/ResponsiveAuthRoute"
import { ForgotPasswordPage } from "@/features/auth/pages/forgotPasswordPage"
import { LoginScreen } from "@/features/auth/pages/loginPage"
import {
  MobileForgotPasswordPage,
  MobileLoginScreen,
  MobilePermissionSetupScreen,
  MobileResetPasswordPage,
  MobileSignupScreen,
  MobileVerifyEmailScreen,
  MobileWelcomeScreen,
} from "@/features/auth/pages/mobile"
import { ResetPasswordPage } from "@/features/auth/pages/resetPasswordPage"
import { SignupScreen } from "@/features/auth/pages/signupPage"
import { EmailVerificationScreen } from "@/features/auth/pages/verifyEmail"

const ExpertsCatalogScreen = lazy(() =>
  import("@/features/catalog/pages/experts").then(m => ({ default: m.ExpertsCatalogScreen })),
)
const RitualistsCatalogScreen = lazy(() =>
  import("@/features/catalog/pages/ritualists").then(m => ({ default: m.RitualistsCatalogScreen })),
)
const MembersCatalogScreen = lazy(() =>
  import("@/features/catalog/pages/members").then(m => ({ default: m.MembersCatalogScreen })),
)
const IntentionsCatalogScreen = lazy(() =>
  import("@/features/catalog/pages/intentions").then(m => ({ default: m.IntentionsCatalogScreen })),
)
const SearchPageScreen = lazy(() =>
  import("@/features/search/pages/SearchPage").then(m => ({ default: m.SearchPage })),
)

import { ChatLayout } from "@/features/chats/components/ChatLayout"
import { AiChatArea } from "@/features/chats/components/chat/AiChatArea"
import { DmChatArea } from "@/features/chats/components/dm/DmChatArea"
import { ChatLandingPage } from "@/features/chats/components/landing/ChatLandingPage"
import { RitualGroupChatArea } from "@/features/chats/components/ritual/RitualGroupChatArea"
import { CommunityDetailScreen } from "@/features/communities/pages/communityDetail"
import { CreateCommunityScreen } from "@/features/communities/pages/createCommunity"
import { EditCommunityScreen } from "@/features/communities/pages/editCommunity"
import { ConnectScreen } from "@/features/connect/pages/connect"
import { DashboardScreen } from "@/features/dashboard/pages/dashboard"
import { NotFoundPage } from "@/features/error/pages/notFoundPage"
import { TooManyRequestsPage } from "@/features/error/pages/tooManyRequestsPage"
import { IntentionDetailScreen } from "@/features/intentions/pages/intentionDetail"
import { LearnAndPracticeScreen } from "@/features/learn-practice/pages/learnAndPractice"
import { MyRitualityScreen } from "@/features/my-rituality/pages/myRituality"
import { RemindersPage } from "@/features/my-rituality/pages/reminders"
import { PermissionSetupScreen } from "@/features/onboarding/pages/setupPermission"
import { WelcomeScreen } from "@/features/onboarding/pages/welcomeUser"
import { EditProfileScreen } from "@/features/profile/pages/editProfile"
import { OtherProfileScreen } from "@/features/profile/pages/otherProfile"
import { ProfileScreen } from "@/features/profile/pages/profile"
import { CreateRitualScreen } from "@/features/rituals/pages/createRitual"
import { EditRitualScreen } from "@/features/rituals/pages/editRitual"

const CreateCoachingScreen = lazy(() =>
  import("@/features/coaching/pages/createCoaching").then(m => ({
    default: m.CreateCoachingScreen,
  })),
)

const EditCoachingScreen = lazy(() =>
  import("@/features/coaching/pages/editCoaching").then(m => ({
    default: m.EditCoachingScreen,
  })),
)

const CoachingSessionRedirect = lazy(() =>
  import("@/features/coaching/pages/coachingSessionRedirect").then(m => ({
    default: m.CoachingSessionRedirect,
  })),
)

import { RitualDetailScreen } from "@/features/rituals/pages/ritualDetail"
import { SettingsScreen } from "@/features/settings/pages/settings"

const TransactionDetailPage = lazy(() =>
  import("@/features/settings/pages/transactionDetail").then(m => ({
    default: m.TransactionDetailPage,
  })),
)

import { RouteErrorBoundary } from "@/components/RouteErrorBoundary"
import {
  consumeAuthReturnTo,
  shouldRunOnboardingBeforeReturn,
} from "@/features/auth/utils/authReturnTo"
import { AppLayout } from "@/layout/AppLayout"
import { AuthAwareLayout } from "@/layout/AuthAwareLayout"
import { ProtectedRoute } from "@/routes/ProtectedRoute"

// OAuth callback handler component
function OAuthCallbackHandler() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    const accessToken = searchParams.get("accessToken")
    const refreshToken = searchParams.get("refreshToken")
    const expiresIn = searchParams.get("expiresIn")
    const profileRequired = searchParams.get("profileRequired")
    const error = searchParams.get("error")
    const deviceId = searchParams.get("deviceId")
    const isGoogle = searchParams.get("google") === "true"
    const isApple = searchParams.get("apple") === "true"
    const googleName = searchParams.get("name")
    const googleEmail = searchParams.get("email")
    const googleUsername = searchParams.get("username")
    const appleName = searchParams.get("name")
    const appleEmail = searchParams.get("email")
    const appleUsername = searchParams.get("username")

    if (error) {
      navigate(`/login?error=${error}`)
      return
    }

    // Handle Google signup flow - new user with pre-filled data
    if (isGoogle && googleName !== null && googleEmail !== null && googleUsername !== null) {
      console.log("[OAuth Callback] Google signup data detected:", {
        googleName,
        googleEmail,
        googleUsername,
      })
      sessionStorage.setItem(
        "google_signup",
        JSON.stringify({
          name: googleName,
          email: googleEmail,
          username: googleUsername,
        }),
      )
      console.log("[OAuth Callback] Redirecting to /signup")
      navigate("/signup")
      return
    }

    // Handle Apple signup flow - new user with pre-filled data
    if (isApple && appleName !== null && appleEmail !== null && appleUsername !== null) {
      sessionStorage.setItem(
        "apple_signup",
        JSON.stringify({
          name: appleName,
          email: appleEmail,
          username: appleUsername,
        }),
      )
      navigate("/signup")
      return
    }

    if (accessToken && refreshToken && expiresIn) {
      // Store tokens in localStorage
      localStorage.setItem("access_token", accessToken)
      localStorage.setItem("refresh_token", refreshToken)
      // Store deviceId if present (for existing users who have a device created)
      if (deviceId) {
        localStorage.setItem("device_id", deviceId)
      }

      // Fetch user profile and set in auth store
      import("@/features/profile/services/user.service")
        .then(({ getProfile }) => {
          getProfile()
            .then(({ user }) => {
              // Import auth store dynamically to avoid circular deps
              import("@/features/auth/store/authStore").then(({ useAuthStore }) => {
                useAuthStore.getState().setUser(user)

                // Run onboarding when the user still needs a profile OR hasn't
                // finished onboarding -- leaving the stored return-to in place so
                // the welcome step (end of onboarding) restores it. Only consume
                // it once the user is fully onboarded; consuming earlier strands
                // them on home after onboarding (issue #565).
                if (
                  shouldRunOnboardingBeforeReturn(
                    profileRequired === "true",
                    Boolean(user.onboardingCompleted),
                  )
                ) {
                  navigate("/onboarding/permissions")
                } else {
                  navigate(consumeAuthReturnTo() ?? "/")
                }
              })
            })
            .catch(err => {
              console.error("[OAuth Callback] Failed to fetch user profile:", err)
              navigate("/login?error=fetch_user_failed")
            })
        })
        .catch(err => {
          console.error("[OAuth Callback] Failed to load profile service:", err)
          navigate("/login?error=fetch_user_failed")
        })
    } else {
      navigate("/login?error=invalid_callback")
    }
  }, [searchParams, navigate])

  return null
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <DashboardScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/my-rituality",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <MyRitualityScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/my-rituality/reminders",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <RemindersPage />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/profile",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <ProfileScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/learn-and-practice",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <LearnAndPracticeScreen />
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/learn-and-practice/intention/:intentionId",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <IntentionDetailScreen />
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/connect",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <ConnectScreen />
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/connect/experts",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <ExpertsCatalogScreen />
            </Suspense>
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/connect/ritualists",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <RitualistsCatalogScreen />
            </Suspense>
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/connect/members",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <MembersCatalogScreen />
            </Suspense>
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/search",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <SearchPageScreen />
            </Suspense>
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/learn-and-practice/intentions",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <IntentionsCatalogScreen />
            </Suspense>
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/chats",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <ChatLayout />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: (
          <RouteErrorBoundary>
            <ChatLandingPage />
          </RouteErrorBoundary>
        ),
      },
      {
        path: "ai/:id",
        element: (
          <RouteErrorBoundary>
            <AiChatArea />
          </RouteErrorBoundary>
        ),
      },
      {
        path: "dm/:conversationId",
        element: (
          <RouteErrorBoundary>
            <DmChatArea />
          </RouteErrorBoundary>
        ),
      },
      {
        path: "ritual/:ritualGroupConversationId",
        element: (
          <RouteErrorBoundary>
            <RitualGroupChatArea />
          </RouteErrorBoundary>
        ),
      },
    ],
  },
  {
    path: "/communities/:id",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <CommunityDetailScreen />
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/communities/create",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <CreateCommunityScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/communities/edit/:id",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <EditCommunityScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/coaching-sessions/:id",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <CoachingSessionRedirect />
          </Suspense>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/coaching-sessions/edit/:id",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <EditCoachingScreen />
            </Suspense>
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/coaching-sessions/create",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <CreateCoachingScreen />
            </Suspense>
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/rituals/create",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <CreateRitualScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/rituals/edit/:id",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <EditRitualScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/rituals/:id",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false}>
          <AuthAwareLayout>
            <RitualDetailScreen />
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/profile/:userId",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={false} requireVerified={false} requireOnboarding={false}>
          <AuthAwareLayout>
            <OtherProfileScreen />
          </AuthAwareLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/profile/edit",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <EditProfileScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/settings",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <SettingsScreen />
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/settings/transactions/:transactionId",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true} requireOnboarding={true}>
          <AppLayout>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <TransactionDetailPage />
            </Suspense>
          </AppLayout>
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/welcome",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireOnboarding={true}>
          <ResponsiveAuthRoute
            desktop={WelcomeScreen}
            mobile={MobileWelcomeScreen}
            showBackButton={false}
            showLogo={false}
            centerContent={true}
            enableSmartNavigation={true}
          />
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/signup",
    element: (
      <RouteErrorBoundary>
        <ResponsiveAuthRoute desktop={SignupScreen} showLogo={false} mobile={MobileSignupScreen} />
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/ref/:username",
    element: (
      <RouteErrorBoundary>
        <ResponsiveAuthRoute desktop={SignupScreen} mobile={MobileSignupScreen} showLogo={false} />
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/login",
    element: (
      <RouteErrorBoundary>
        <ResponsiveAuthRoute
          desktop={LoginScreen}
          mobile={MobileLoginScreen}
          showBackButton={false}
        />
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <RouteErrorBoundary>
        <ResponsiveAuthRoute
          desktop={ForgotPasswordPage}
          mobile={MobileForgotPasswordPage}
          showLogo={false}
          centerContent={true}
        />
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <RouteErrorBoundary>
        <ResponsiveAuthRoute
          desktop={ResetPasswordPage}
          mobile={MobileResetPasswordPage}
          showLogo={false}
          centerContent={true}
          onNavigateBack={() => {
            window.location.href = "/login"
          }}
        />
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/verify-email",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true}>
          <ResponsiveAuthRoute
            desktop={EmailVerificationScreen}
            mobile={MobileVerifyEmailScreen}
            showBackButton={true}
            showLogo={false}
            enableSmartNavigation={true}
            onNavigateBack={() => {
              window.location.href = "/login"
            }}
          />
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/onboarding/permissions",
    element: (
      <RouteErrorBoundary>
        <ProtectedRoute requireAuth={true} requireVerified={true}>
          <ResponsiveAuthRoute
            desktop={PermissionSetupScreen}
            mobile={MobilePermissionSetupScreen}
            showBackButton={false}
            showLogo={false}
            enableSmartNavigation={true}
          />
        </ProtectedRoute>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/error/too-many-requests",
    element: (
      <RouteErrorBoundary>
        <TooManyRequestsPage />
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/oauth/callback",
    element: (
      <RouteErrorBoundary>
        <OAuthCallbackHandler />
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/not-found",
    element: (
      <RouteErrorBoundary>
        <NotFoundPage />
      </RouteErrorBoundary>
    ),
  },
  {
    path: "*",
    element: (
      <RouteErrorBoundary>
        <NotFoundPage />
      </RouteErrorBoundary>
    ),
  },
])
